import React from 'react';

/**
 * Global Error Boundary — catches React rendering crashes.
 * For DOM reconciliation errors (insertBefore, removeChild), it auto-reloads
 * to clear stale cached bundles. For other errors, shows a friendly screen.
 */
export class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        console.error('[ErrorBoundary]', error, info);
        this.setState({ errorInfo: info });

        // Auto-reload for DOM reconciliation errors (stale cache)
        const msg = error?.message || '';
        const isDOMError = msg.includes('insertBefore') || 
                          msg.includes('removeChild') || 
                          msg.includes('appendChild');
        
        if (isDOMError) {
            const RELOAD_KEY = '__pr_reload';
            const RELOAD_COOLDOWN = 30000;
            const last = parseInt(localStorage.getItem(RELOAD_KEY) || '0', 10);
            const now = Date.now();
            
            if ((now - last) > RELOAD_COOLDOWN) {
                localStorage.setItem(RELOAD_KEY, String(now));
                // Clear caches
                if ('caches' in window) {
                    caches.keys().then(names => names.forEach(n => caches.delete(n)));
                }
                window.location.reload(true);
                return; // Don't render error UI, we're reloading
            }
        }
    }

    handleReload = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
        // Clear caches before navigating
        if ('caches' in window) {
            caches.keys().then(names => names.forEach(n => caches.delete(n)));
        }
        localStorage.removeItem('__pr_reload');
        window.location.href = '/';
    };

    render() {
        if (this.state.hasError) {
            const errorMsg = this.state.error?.message || 'Error desconocido';
            
            return (
                <div style={{
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, #001944 0%, #4c56af 100%)',
                    color: 'white',
                    padding: '24px',
                    textAlign: 'center',
                    fontFamily: 'Inter, system-ui, sans-serif'
                }}>
                    <div style={{ fontSize: 56, marginBottom: 16 }}>⚠️</div>
                    <h1 style={{ fontSize: 24, fontWeight: 800, marginBottom: 8 }}>
                        Algo salió mal
                    </h1>
                    <p style={{ color: 'rgba(255,255,255,0.7)', marginBottom: 16, maxWidth: 340 }}>
                        Ocurrió un error inesperado. Tus datos están seguros.
                    </p>
                    <p style={{ 
                        color: 'rgba(255,255,255,0.4)', 
                        fontSize: 11, 
                        marginBottom: 32, 
                        maxWidth: 400,
                        wordBreak: 'break-word',
                        fontFamily: 'monospace'
                    }}>
                        {errorMsg}
                    </p>
                    <button
                        onClick={this.handleReload}
                        style={{
                            background: 'white',
                            color: '#001944',
                            border: 'none',
                            borderRadius: 12,
                            padding: '12px 32px',
                            fontWeight: 800,
                            fontSize: 16,
                            cursor: 'pointer'
                        }}
                    >
                        Volver al inicio
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

